import type { ExitQuestion } from "@/testmeta.types.ts";
import { Suspense, useEffect, useState } from "react";
import { useInputState, useDebouncedValue } from '@mantine/hooks';
import { MetaCompBlks } from "./meta.components.tsx";
import { DynamicForm, DynamicFormProps, DynamicFormRef } from "./dynamic.form.tsx";


export interface DynamicQuestionProp extends ExitQuestion {
    setValue?: (value: string | boolean | number | string[] | number[] | undefined) => void;
}

interface TimeComponentProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: string[];
}

function TimeComponent({ label, value, onChange, options }: TimeComponentProps) {
    const [timeProps, setTimeProps] = useState<any>(null);
    
    useEffect(() => {
        const loadTimeProps = async () => {
            const { Fieldset } = await import('@mantine/core');
            const parsedOptions = await MetaCompBlks.time.parseOptions(options);
            setTimeProps({ Fieldset, parsedOptions });
        };
        loadTimeProps();
    }, [options]);

    if (!timeProps) {
        return <div>Loading time component...</div>;
    }

    const { Fieldset, parsedOptions } = timeProps;
    return (
        <Fieldset legend={label}>
            <MetaCompBlks.time.comp
                value={value}
                onChange={onChange}
                {...parsedOptions}
            />
        </Fieldset>
    );
}



export function DynamicComponent(props: DynamicQuestionProp) {
    const [value, setValue] = useInputState<string | boolean | number | string[] | number[] | undefined>(props.value);
    const [debouncedValue] = useDebouncedValue(value, 500);
    useEffect(() => {
        props.setValue?.(debouncedValue);
    }, [debouncedValue, props.setValue]);
    const getComp = () => {
        switch (props.qType) {
            case 'long':
                return <MetaCompBlks.long.comp
                    label={props.label}
                    description={props.secondaryLabel}
                    value={value as string}
                    onChange={setValue}
                    {...MetaCompBlks.long.parseOptions(props.options || [])}
                />
            case 'select':
                return <MetaCompBlks.select.comp
                    label={props.label}
                    description={props.secondaryLabel}
                    value={value as string}
                    onChange={setValue}
                    {...MetaCompBlks.select.parseOptions(props.options || [])}
                />
            case 'checkbox':
                return <MetaCompBlks.checkbox.comp
                    onChange={setValue}
                    autoContrast
                    checked={value as boolean}
                >{props.label}</MetaCompBlks.checkbox.comp>
            case 'color':
                return <MetaCompBlks.color.comp
                    label={props.label}
                    description={props.secondaryLabel}
                    value={value as string}
                    onChange={setValue}
                    {...MetaCompBlks.color.parseOptions(props.options || [])}
                />
            case 'time':
                return <TimeComponent 
                    label={props.label}
                    value={value as string}
                    onChange={setValue}
                    options={props.options || []}
                />
            case 'date':
                return <MetaCompBlks.date.comp
                    label={props.label}
                    description={props.secondaryLabel}
                    value={value as string}
                    onChange={setValue}
                    {...MetaCompBlks.date.parseOptions(props.options || [])}
                />
            case 'file':
                return <MetaCompBlks.file.comp
                    label={props.label}
                    description={props.secondaryLabel}
                    value={value as string}
                    onChange={setValue}
                    {...MetaCompBlks.file.parseOptions(props.options || [])}
                />
            case 'rating':
                return <MetaCompBlks.rating.comp
                    label={props.label}
                    description={props.secondaryLabel}
                    value={value as string}
                    onChange={setValue}
                    {...MetaCompBlks.rating.parseOptions(props.options || [])}
                />
            case 'text':
            default:
                return <MetaCompBlks.text.comp
                    label={props.label}
                    description={props.secondaryLabel}
                    value={value as string}
                    onChange={setValue}
                    {...MetaCompBlks.text.parseOptions(props.options || [])}
                />
        }
    }
    return <Suspense fallback={<div>Loading...</div>}>{getComp()}</Suspense>
}

export namespace DynamicComponent {
    export function Form(props: DynamicFormProps & DynamicFormRef) {
        return <DynamicForm {...props} />
    }
}